/* eslint-disable react-native/no-inline-styles */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import ColorWrapper from '@styles/colors';
import BaseFrame from './baseFrame';

import { languages, getSecureValue } from '@helpers';
import { FeatureFlagsAvailable } from '@components/featureFlags';
import { updateUserProfile } from '@reducers/membership';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { updateLabelSwitchedOn } from '@reducers/language';
import { LABEL_PATTERN, LANGUAGE_LABELS_FEATURE_FLAG } from '@v2/pages/common/constants/base.constants';

let languageHeaderClickCount = 0;

export const getLanguageTileByValue = (value) => {
  const item = languages.find((item) => item.value === value);
  return item && item.name ? item.name : '';
};

const BaseModal = (props) => {
  const { updateLanguage, lg, featureFlags, userInfo, updateUserProfile, updateLabelSwitchedOn, labelSwitchedOn } = props;
  const [data, setData] = useState(languages.filter((i) => (i.flag ? FeatureFlagsAvailable(i.flag, featureFlags) : true)));
  const [initialLanguage] = useState(lg);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showFooterOverride, setShowFooterOverride] = useState(false);

  useEffect(() => {
    setSystemLanguage();
  }, []);

  useEffect(() => {
    const filteredData = languages.filter((i) => {
      if (!i.flag) return true;
      // Override pseudolocale flag check when footer override is active
      if (i.flag === 'settings-pseudolocale-language' && showFooterOverride) {
        return true;
      }
      return FeatureFlagsAvailable(i.flag, featureFlags);
    });
    setData(filteredData);
    // Re-apply system language labeling
    const system = getSecureValue('systemLanguage');
    const newData = filteredData.map((l) => {
      if (l.value === system) {
        return { ...l, title: 'System default' };
      }
      return l;
    });
    setData(newData);
  }, [showFooterOverride, featureFlags]);

  const selectedLanguage = lg;
  const onSelectLanguage = (item) => {
    updateLanguage(item.value);
  };

  const handleLanguageHeaderClick = () => {
    if (languageHeaderClickCount === 4) {
      languageHeaderClickCount = 0;
      setShowFooterOverride(true);
    } else {
      languageHeaderClickCount += 1;
    }
  };

  const onSubmit = async () => {
    setSubmitLoading(true);
    await updateUserProfile({
      emailAddress: userInfo?.emailAddress,
      userProfile: {
        ...userInfo?.userProfile,
        preferedLanguage: selectedLanguage,
      },
    });
    trackEvent(TRACKING_EVENTS.SETTINGS, {
      action: 'languageChanged',
      oldLanguage: initialLanguage,
      newLanguage: selectedLanguage,
    });
    setSubmitLoading(false);
  };

  const onCancelLanguage = () => {
    updateLanguage(initialLanguage);
  };

  const setSystemLanguage = async () => {
    const system = getSecureValue('systemLanguage');
    const newData = data.map((l) => {
      if (l.value === system) {
        return {
          ...l,
          title: 'System default',
        };
      }
      return l;
    });
    setData(newData);
  };

  const filteredLanguages = labelSwitchedOn
    ? data.filter((l) => l.value.startsWith(LABEL_PATTERN))
    : data.filter((l) => !l.value.startsWith(LABEL_PATTERN));

  const isLanguageLabelsAvailable = FeatureFlagsAvailable(LANGUAGE_LABELS_FEATURE_FLAG, featureFlags) || showFooterOverride;

  return (
    <BaseFrame
      {...{
        data: filteredLanguages,
        onSubmit: onSubmit,
        onSelect: onSelectLanguage,
        onCancel: onCancelLanguage,
        selected: selectedLanguage,
        header: 'Language',
        submitLoading,
        onHeaderClick: handleLanguageHeaderClick,
        ...(isLanguageLabelsAvailable
          ? {
              footerBlockConfig: {
                title: 'Display Label Numbers',
                description:
                  'Display language label numbers to assist with translation. This setting will automatically reset after restarting the app or logging in again.',
                action: updateLabelSwitchedOn,
                height: 55,
                value: labelSwitchedOn,
              },
            }
          : {}),
        ...props,
      }}
    />
  );
};

export default connect(({ user: { featureFlags, userInfo }, language: { labelSwitchedOn } }) => ({ featureFlags, userInfo, labelSwitchedOn }), {
  updateUserProfile,
  updateLabelSwitchedOn,
})(ColorWrapper(BaseModal));
